import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { PageTitle, useLayout } from '../../../_metronic/layout/core';
import { DefaultTitle } from '../../../_metronic/layout/components/header/page-title/DefaultTitle';
import { useAuth } from '../../contexts/AuthProvider';
import { IContaBancaria, IResumoBancario, ISugestaoContaBancaria } from '../../interfaces/IModuloBancario';
import EmpresasService from '../../services/EmpresasService';
import ModuloBancarioService from '../../services/ModuloBancarioService';
import RelatoriosService from '../../services/RelatoriosService';
import ArquivosModulo, { TipoArquivo, TIPOS_ARQUIVO } from './arquivos-modulo';
import ContasBancarias from './contas-bancarias';
import { CabecalhoEtapa, calcularEtapas, ConsultaId, EscolherCompetencia, EtapaId, RodapeEtapa, StepperModulo } from './etapas-modulo';
import FechamentoBancario from './fechamento-bancario';
import GuiaModulo from './guia-modulo';
import IndicadoresBancarios from './indicadores-bancarios';
import MinutasBancarias from './minutas-bancarias';
import ModalContaBancaria from './modal-conta-bancaria';
import SetupBancario from './setup-bancario';
import TrilhaBancaria from './trilha-bancaria';

// Módulo Bancário: uma etapa por vez, na ordem do trabalho. Só a etapa aberta consulta o servidor;
// o resumo mantém a situação de todas e as contas bloqueadas.

const INTERVALO_RESUMO_MS = 30000;
const STORAGE_EMPRESA = 'modulo-bancario:empresa';
const STORAGE_ESTEIRA = 'modulo-bancario:esteira';
const STORAGE_GUIA = 'modulo-bancario:guia-visto';
const ABAS_VALIDAS: (EtapaId | ConsultaId)[] = ['preparar', 'arquivos', 'minutas', 'fechar', 'indicadores', 'trilha'];

const lerStorage = (chave: string): string | null => {
    try {
        return localStorage.getItem(chave);
    } catch {
        return null;
    }
};

const gravarStorage = (chave: string, valor: string) => {
    try {
        localStorage.setItem(chave, valor);
    } catch {
        // Sem armazenamento, a tela só deixa de lembrar a escolha.
    }
};

function ModuloBancario() {
    const { usuario } = useAuth();
    const { classes } = useLayout();
    const history = useHistory();
    const location = useLocation();

    const [empresas, setEmpresas] = useState<any[]>([]);
    const [empresaId, setEmpresaId] = useState<number>(0);
    const [esteiras, setEsteiras] = useState<any[]>([]);
    const [esteiraId, setEsteiraId] = useState<number>(0);
    const [competencia, setCompetencia] = useState<string>('');

    const [contas, setContas] = useState<IContaBancaria[]>([]);
    const [resumo, setResumo] = useState<IResumoBancario | null>(null);
    const [guiaAberto, setGuiaAberto] = useState(false);
    const [modalConta, setModalConta] = useState<{ conta?: IContaBancaria | null; sugestao?: { arquivoId: number; dados: ISugestaoContaBancaria } | null } | null>(null);
    const [versaoMinutas, setVersaoMinutas] = useState(0);
    const [versaoFechamento, setVersaoFechamento] = useState(0);

    // ---- Etapa aberta, na URL: sobrevive ao recarregar e pode ser compartilhada ---------------------

    const consulta = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const parametroEtapa = consulta.get('etapa') as EtapaId | ConsultaId | null;
    const ativa: EtapaId | ConsultaId = parametroEtapa && ABAS_VALIDAS.includes(parametroEtapa) ? parametroEtapa : 'preparar';
    const parametroTipo = consulta.get('tipo') as TipoArquivo | null;
    const tipoArquivo: TipoArquivo = parametroTipo && TIPOS_ARQUIVO.includes(parametroTipo) ? parametroTipo : 'extratos';

    const irPara = useCallback((etapa: EtapaId | ConsultaId, tipo?: TipoArquivo) => {
        const params = new URLSearchParams({ etapa });
        if (etapa === 'arquivos' && tipo) params.set('tipo', tipo);
        history.push({ search: params.toString() });
    }, [history]);

    // ---- Primeiro acesso: o guia abre sozinho uma vez por navegador ---------------------------------

    useEffect(() => {
        if (lerStorage(STORAGE_GUIA)) return;
        setGuiaAberto(true);
        gravarStorage(STORAGE_GUIA, '1');
    }, []);

    // ---- Empresa / esteira / competência ---------------------------------------------------------

    const selecionarEmpresa = useCallback((id: number) => {
        setEmpresaId(id);
        setEsteiras([]);
        setEsteiraId(0);
        setCompetencia('');
        setContas([]);
        setResumo(null);
        if (!id) return;
        EmpresasService.obterEsteiras(id).then(resp => {
            setEsteiras(resp);
            const local = parseInt(lerStorage(STORAGE_ESTEIRA) ?? '0');
            if (resp.find((e: any) => e.id === local)) setEsteiraId(local);
        });
    }, []);

    useEffect(() => {
        if (!usuario) return;
        RelatoriosService.obterEmpresasPorClienteID(0, resp => {
            setEmpresas(resp);
            const local = parseInt(lerStorage(STORAGE_EMPRESA) ?? '0');
            if (resp.find((e: any) => e.empresaID === local)) selecionarEmpresa(local);
        });
    }, [usuario, selecionarEmpresa]);

    const esteiraAtual = esteiras.find(e => e.id === esteiraId);

    const competencias = useMemo(() => {
        if (!esteiraAtual) return [];
        const lista: string[] = [];
        const fim = moment(esteiraAtual.periodoFim).startOf('month');
        for (let m = moment(esteiraAtual.periodoInicio).startOf('month'); m.isSameOrBefore(fim); m.add(1, 'month'))
            lista.push(m.format('YYYY-MM'));
        return lista;
    }, [esteiraAtual]);

    // ---- Dados -------------------------------------------------------------------------------------

    const [ano, mes] = competencia ? competencia.split('-').map(Number) : [undefined, undefined];

    const buscarContas = useCallback(async () => {
        if (!esteiraId) return;
        try {
            // Só as contas interessam aqui; os extratos são da etapa Arquivos.
            setContas((await ModuloBancarioService.listar(esteiraId, ano, mes, { tamanhoPagina: 1 })).contas);
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? err?.response?.data?.message ?? 'Não foi possível carregar o Módulo Bancário.', icon: 'error' });
        }
    }, [esteiraId, ano, mes]);

    // Só a resposta da consulta mais recente vale: trocar de mês não pode trazer de volta a do mês anterior.
    const ultimaConsultaResumo = useRef(0);
    const buscarResumo = useCallback(async () => {
        if (!esteiraId) return;
        const consultaAtual = ++ultimaConsultaResumo.current;
        try {
            const dados = await ModuloBancarioService.resumo(esteiraId, ano, mes);
            if (consultaAtual === ultimaConsultaResumo.current) setResumo(dados);
        } catch {
            // A situação das etapas é orientação: a próxima consulta tenta de novo.
        }
    }, [esteiraId, ano, mes]);

    useEffect(() => { buscarContas(); }, [buscarContas]);
    useEffect(() => {
        setResumo(null);
        buscarResumo();
    }, [buscarResumo]);

    // A próxima consulta só é agendada depois da anterior responder, para não acumular com o banco lento.
    useEffect(() => {
        if (!esteiraId) return;
        let ativo = true;
        let id: ReturnType<typeof setTimeout>;
        const ciclo = async () => {
            if (!document.hidden) await buscarResumo();
            if (ativo) id = setTimeout(ciclo, INTERVALO_RESUMO_MS);
        };
        id = setTimeout(ciclo, INTERVALO_RESUMO_MS);
        return () => {
            ativo = false;
            clearTimeout(id);
        };
    }, [esteiraId, buscarResumo]);

    const aoAlterar = useCallback(() => {
        setVersaoFechamento(v => v + 1);
        buscarResumo();
        buscarContas();
    }, [buscarResumo, buscarContas]);

    const aoCarregarMinutas = useCallback(() => {
        setVersaoMinutas(v => v + 1);
        buscarResumo();
    }, [buscarResumo]);

    // A geração também nasce de documentos e do Setup: quem está aberto precisa acompanhar.
    const ultimaOperacao = resumo?.ultimaOperacaoId;
    useEffect(() => {
        if (ultimaOperacao) setVersaoMinutas(v => v + 1);
    }, [ultimaOperacao]);

    // DEVN3-771: depois de uma reimportação, leva o módulo para a esteira ativa e confere o razão.
    // Se falhar, nada se perde: o botão "Conferir razão" faz o mesmo e mostra o erro.
    const aoAlterarAtual = useRef(aoAlterar);
    aoAlterarAtual.current = aoAlterar;
    useEffect(() => {
        if (esteiraId) ModuloBancarioService.conferirRazao(esteiraId, true).then(() => aoAlterarAtual.current()).catch(() => undefined);
    }, [esteiraId]);

    const etapas = useMemo(() => calcularEtapas(resumo, competencia), [resumo, competencia]);
    const etapaAtual = etapas.find(e => e.id === ativa);
    const contasBloqueadas = resumo?.contasBloqueadas ?? [];

    return (
        <>
            <PageTitle breadcrumbs={[]}>Módulo Bancário</PageTitle>
            <div className='toolbar' id='kt_toolbar'>
                <div id='kt_toolbar_container' className={clsx(classes.toolbarContainer.join(' '), 'd-flex flex-stack')}>
                    <DefaultTitle />
                    <button className='btn btn-sm btn-light-primary' onClick={() => setGuiaAberto(true)}>
                        <i className='fas fa-question-circle me-1'></i>Como funciona
                    </button>
                </div>
            </div>

            <div className='card card-flush mb-4'>
                <div className='card-body py-4'>
                    <div className='d-flex flex-wrap align-items-end gap-4'>
                        <div className='d-flex flex-column' style={{ minWidth: 260, flex: '1 1 260px' }}>
                            <label className='form-label fw-semibold text-gray-700 mb-1'>
                                <i className='fas fa-building me-1 text-primary'></i>Empresa
                            </label>
                            <Select
                                placeholder='Selecione uma Empresa'
                                isClearable
                                value={empresas.filter(e => e.empresaID === empresaId).map(e => ({ label: e.razaoSocial, value: e.empresaID }))[0] ?? null}
                                options={empresas.map(e => ({
                                    label: `${e.razaoSocial}${e.cnpj && e.cnpj.replace(/[^A-Za-z0-9]/g, '').substring(8, 12) !== '0001' ? ` - ${e.cnpj}` : ''}`,
                                    value: e.empresaID,
                                }))}
                                onChange={op => {
                                    const id = op ? Number(op.value) : 0;
                                    gravarStorage(STORAGE_EMPRESA, String(id));
                                    selecionarEmpresa(id);
                                }}
                                noOptionsMessage={() => 'Nenhuma empresa encontrada'}
                            />
                        </div>
                        <div className='d-flex flex-column' style={{ minWidth: 200, flex: '1 1 200px' }}>
                            <label className='form-label fw-semibold text-gray-700 mb-1'>
                                <i className='fas fa-calendar-alt me-1 text-primary'></i>Período
                            </label>
                            <select className='form-select' value={esteiraId} disabled={!empresaId}
                                onChange={ev => {
                                    gravarStorage(STORAGE_ESTEIRA, ev.target.value);
                                    setEsteiraId(Number(ev.target.value));
                                    setCompetencia('');
                                }}>
                                <option value={0}>Selecione um período</option>
                                {esteiras.map(e => <option key={e.id} value={e.id}>{e.periodoInicioString} - {e.periodoFimString}</option>)}
                            </select>
                        </div>
                        <div className='d-flex flex-column' style={{ minWidth: 160, flex: '0 1 180px' }}>
                            <label className='form-label fw-semibold text-gray-700 mb-1'>
                                <i className='fas fa-calendar-day me-1 text-primary'></i>Competência
                            </label>
                            <select className='form-select' value={competencia} disabled={!esteiraId}
                                onChange={ev => setCompetencia(ev.target.value)}>
                                <option value=''>Todas</option>
                                {competencias.map(c => <option key={c} value={c}>{moment(c, 'YYYY-MM').format('MM/YYYY')}</option>)}
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {esteiraId === 0 && (
                <div className='card card-flush mb-4'>
                    <div className='card-body text-center py-10'>
                        <i className='fas fa-university fs-1 text-primary mb-3'></i>
                        <h3 className='fw-bold mb-2'>Escolha a empresa e o período</h3>
                        <div className='text-muted mb-5'>O módulo trabalha sobre o ECD ou o razão já importado para o período.</div>
                        <button className='btn btn-sm btn-light-primary' onClick={() => setGuiaAberto(true)}>Ver como funciona</button>
                    </div>
                </div>
            )}

            {esteiraId > 0 && (
                <>
                    <StepperModulo etapas={etapas} ativa={ativa} onEscolher={id => irPara(id)} />

                    {etapaAtual && <CabecalhoEtapa etapa={etapaAtual} />}

                    {ativa === 'preparar' && (
                        <>
                            <ContasBancarias contas={contas} onNova={() => setModalConta({})} onEditar={conta => setModalConta({ conta })} />
                            <SetupBancario esteiraId={esteiraId} contas={contas} onAlterou={buscarResumo} />
                        </>
                    )}

                    {ativa === 'arquivos' && (
                        <ArquivosModulo
                            tipo={tipoArquivo}
                            onTipo={tipo => irPara('arquivos', tipo)}
                            esteiraId={esteiraId}
                            competencia={competencia}
                            competencias={competencias}
                            onCompetencia={setCompetencia}
                            contas={contas}
                            resumo={resumo}
                            versao={versaoFechamento}
                            versaoMinutas={versaoMinutas}
                            onAlterou={aoAlterar}
                            onCadastrarConta={sugestao => setModalConta({ sugestao })}
                        />
                    )}

                    {(ativa === 'minutas' || ativa === 'fechar') && !competencia && (
                        <EscolherCompetencia competencias={competencias} onEscolher={setCompetencia}
                            motivo={ativa === 'minutas' ? 'As minutas são geradas e aprovadas por mês.' : 'O fechamento é por conta bancária e mês.'} />
                    )}

                    {ativa === 'minutas' && competencia && (
                        <MinutasBancarias esteiraId={esteiraId} competencia={competencia} contas={contas} contasBloqueadas={contasBloqueadas} onCarregou={aoCarregarMinutas} versaoFechamento={versaoFechamento} />
                    )}

                    {ativa === 'fechar' && competencia && (
                        <FechamentoBancario esteiraId={esteiraId} competencia={competencia} contas={contas} versaoMinutas={versaoMinutas} onAlterou={aoAlterar} />
                    )}

                    {ativa === 'indicadores' && (
                        <IndicadoresBancarios esteiraId={esteiraId} competencia={competencia} contas={contas} versaoMinutas={versaoMinutas} />
                    )}

                    {ativa === 'trilha' && (
                        <TrilhaBancaria esteiraId={esteiraId} competencia={competencia} contas={contas} />
                    )}

                    {etapaAtual && <RodapeEtapa etapas={etapas} ativa={etapaAtual.id} onEscolher={id => irPara(id)} />}
                </>
            )}

            {modalConta && (
                <ModalContaBancaria
                    show
                    empresaId={empresaId}
                    esteiraId={esteiraId}
                    conta={modalConta.conta}
                    sugestao={modalConta.sugestao}
                    onHide={() => setModalConta(null)}
                    onSalvo={aoAlterar}
                />
            )}

            {guiaAberto && <GuiaModulo onFechar={() => setGuiaAberto(false)} />}
        </>
    );
}

export default ModuloBancario;
